import { fr } from "@codegouvfr/react-dsfr";
import Button from "@codegouvfr/react-dsfr/Button";
import { bbox, centroid } from "@turf/turf";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  type Dispatch,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { MapGeoJSONFeature, MapInstance, MapLayerMouseEvent, MapRef, MapSourceDataEvent } from "react-map-gl/maplibre";
import Map, {
  Marker,
  NavigationControl,
  type StyleSpecification,
} from "react-map-gl/maplibre";
import { tss } from "tss-react/dsfr";
import usePrevious from "../../hooks/previous";
import { buildExclusiveDiagnosticSearch } from "../../utils/diagnosticSearchParams";
import { computeParcelleSiblings, updateFeatureState } from "../../utils/map";
import {
  getIconFromNoiseCategorySlug,
  getRiskFromScore,
  getZoomFromGeometry,
} from "../../utils/tools";
import type {
  DiagnosticItem,
  DiagnosticResponseError,
  NoiseSourceIntersection,
} from "../../utils/types";
import { type AddressFeature } from "../search/AddressSearch";
import NoisePinModal, { noisePinModal } from "./NoisePinModal";
import orthoStyle from "./styles/ortho.json";
import { useDiagnostics } from "./useDiagnostics";
import {
  useHoverFeatureState,
  useOutlinePreviousSelection,
} from "./useMapFeatureState";
import { trackMatomoEvent } from "../../utils/matomo";

const interactiveLayerIds = ["parcelles-fill"];

// Bordeaux center
const defaultViewState = {
  longitude: -0.57918,
  latitude: 44.837789,
  zoom: 12,
};


export type HoverInfo = {
  longitude: number;
  latitude: number;
  feature: MapGeoJSONFeature;
};

type MapComponentProps = {
  noisePins: NoiseSourceIntersection[];
  onDiagnosticsChange: (newDiagnostics: DiagnosticItem[]) => void;
  onLoading: (loading: boolean) => void;
  onReady: () => void;
  onReset: () => void;
  onErrorChange: (error?: DiagnosticResponseError) => void;
  onAddressSelected: (feature: AddressFeature) => void;
};

export interface ExposedMapMethods {
  map: MapInstance | undefined;
  parcelle: MapGeoJSONFeature | null;
  setParcelle: Dispatch<React.SetStateAction<MapGeoJSONFeature | null>> | null;
  setParcelleSiblings: Dispatch<
    React.SetStateAction<MapGeoJSONFeature[]>
  > | null;
}

const MapComponent = forwardRef<ExposedMapMethods, MapComponentProps>(
  (
    {
      noisePins,
      onDiagnosticsChange,
      onLoading,
      onReady,
      onReset,
      onErrorChange,
      onAddressSelected,
    },
    ref,
  ) => {
    const { cx, classes } = useStyles();

    const [parcelle, setParcelle] = useState<MapGeoJSONFeature | null>(null);
    const [parcelleSiblings, setParcelleSiblings] = useState<
      MapGeoJSONFeature[]
    >([]);
    const [hovered, setHovered] = useState<HoverInfo | null>(null);
    const [cursor, setCursor] = useState("default");
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [openTilesMapNotLoading, setOpenTilesMapNotLoading] = useState(false);

    const internalMapRef = useRef<MapRef>(null);
    const [map, setMap] = useState<MapInstance>();

    const [openMapTilesLoaded, setOpenMapTilesLoaded] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [selectedNoisePin, setSelectedNoisePin] =
      useState<NoiseSourceIntersection | null>(null);
    const [zoom, setZoom] = useState(defaultViewState.zoom);

    const prevHovered = usePrevious(hovered);
    const prevSelected = usePrevious(parcelle);

    const { response, error, isLoading } = useDiagnostics(
      parcelle,
      parcelleSiblings,
    );

    useImperativeHandle(
      ref,
      () => ({
        map,
        parcelle,
        setParcelle,
        setParcelleSiblings,
      }),
      [map, parcelle, setParcelle, setParcelleSiblings],
    );

    useHoverFeatureState(map, hovered, prevHovered);
    useOutlinePreviousSelection(map, parcelle, prevSelected);

    const mapRef = useCallback((ref: MapRef) => {
      if (ref) {
        internalMapRef.current = ref;
        setMap(ref.getMap());
      }
    }, []);

    const onMapLoad = () => {
      setIsMapLoaded(true);
    };

    const onMapSourceData = (e: MapSourceDataEvent) => {
      if ((((e.source as any)?.url as string) || "").includes("openmaptiles")) {
        if (e.isSourceLoaded) {
          setOpenMapTilesLoaded(true);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        }
      }
    };

    const onClick = useCallback(
      (event: MapLayerMouseEvent) => {
        event.originalEvent.stopPropagation();
        const feature = event.features?.[0];

        if (feature?.layer.id === "parcelles-fill" && map) {
          if (parcelle?.id) {
            updateFeatureState(map, parcelle.id, { selected: false });
          }

          const { clickedParcelle, nearbySiblings } = computeParcelleSiblings(
            map,
            feature as MapGeoJSONFeature,
            1000,
          );

          if (clickedParcelle?.id) {
            updateFeatureState(map, clickedParcelle.id, { selected: true });
          }

          setParcelleSiblings(nearbySiblings);
          setParcelle(clickedParcelle);
          trackMatomoEvent("Action", "Map clic search", `diagnostic-map-clic-${parcelle?.id}`)
        } else {
          setParcelle(null);
        }
      },
      [map, parcelle],
    );

    const onHover = (event: MapLayerMouseEvent) => {
      event.originalEvent.stopPropagation();
      const feature = event.features?.[0];
      const { lng, lat } = event.lngLat;

      setHovered(feature ? { longitude: lng, latitude: lat, feature } : null);

      if (event.type === "mouseenter") {
        setCursor("pointer");
      } else if (event.type === "mouseleave") {
        setCursor("default");
      }
    };

    const replaceSearchParams = (parcelle: MapGeoJSONFeature) => {
      const queryParcelle = {
        geometry: parcelle._geometry,
        properties: {
          ...parcelle.properties,
          code_insee: parcelle.properties.commune,
          idu: parcelle.properties.id,
        },
        bbox: bbox(parcelle._geometry),
      };
      const params = buildExclusiveDiagnosticSearch(
        "parcelle",
        queryParcelle,
        window.location.search,
      );
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, "", newUrl);
    };

    const handleDiagnostics = useCallback(() => {
      if (!map || !parcelle || !response?.diagnostics) return;

      onDiagnosticsChange(response.diagnostics);

      replaceSearchParams(parcelle);

      const allParcelles = [...parcelleSiblings, parcelle];

      response.diagnostics.forEach((item: any) => {
        const targetParcelle = allParcelles.find((p) => {
          const {
            commune: code_insee,
            section: tmpSection,
            numero: tmpNumero,
          } = p.properties;
          const numero = tmpNumero.toString().padStart(4, "0");
          const section = tmpSection.toString().padStart(2, "0");

          return (
            code_insee === item.parcelle.code_insee &&
            section === item.parcelle.section &&
            numero === item.parcelle.numero
          );
        });

        if (targetParcelle) {
          const featureState = {
            risk: item.diagnostic.flags.hasNoisemapWarning
              ? -1
              : getRiskFromScore(item.diagnostic.score),
            ...(targetParcelle.id === parcelle.id
              ? { selected: false, outlinePrimary: true }
              : { outlineSecondary: true }),
          };

          if (targetParcelle.id) {
            updateFeatureState(map, targetParcelle.id, featureState);
          }
        }
      });
    }, [map, parcelle, parcelleSiblings, response]);

    const handleNoisePinClick = useCallback((pin: NoiseSourceIntersection) => {
      setSelectedNoisePin(pin);
      noisePinModal.open();
    }, []);

    const onZoomChange = useCallback((evt: any) => {
      setZoom(evt.viewState.zoom);
    }, []);

    useEffect(() => {
      onLoading(isLoading || !openMapTilesLoaded);
    }, [isLoading, openMapTilesLoaded]);

    useEffect(() => {
      if (isMapLoaded && response?.diagnostics) {
        handleDiagnostics();
      }
    }, [isMapLoaded, response]);

    useEffect(() => {
      if (isMapLoaded) {
        onReady();
      }
    }, [isMapLoaded]);

    useEffect(() => {
      if (onErrorChange) {
        onErrorChange(error);
      }
    }, [error]);

    useEffect(() => {
      const geometry = parcelle?.geometry as any;
      if (!map || !parcelle || !geometry?.coordinates) return;

      const centerPoint = centroid(parcelle);
      const [lng, lat] = centerPoint.geometry.coordinates;

      const offsetLng = lng - 0;

      map.flyTo({
        center: [offsetLng, lat],
        zoom: getZoomFromGeometry(parcelle._geometry),
        essential: true,
      });
    }, [map, parcelle]);

    useEffect(() => {
      timeoutRef.current = setTimeout(() => {
        if (!openMapTilesLoaded) {
          setOpenTilesMapNotLoading(true);
          setOpenMapTilesLoaded(true);
        }
      }, 5000);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <div className={classes.container}>
        {openTilesMapNotLoading && (
          <div className={classes.bigAlert}>
            <p className={fr.cx("fr-text--xl")}>❌</p>
            <p className={fr.cx("fr-mb-3v")}>
              <b>Erreur lors du chargement des fonds de carte.</b>
            </p>
            <p>
              Cela est dû à un problème avec notre fournisseur externe
              (GeoGouv).
              <br /> Veuillez réessayer un peu plus tard.
            </p>
            <Button
              iconId="ri-restart-line"
              size="small"
              onClick={() => {
                window.location.reload();
              }}
            >
              Essayer de recharger la page
            </Button>
          </div>
        )}
        <Map
          id="map"
          ref={mapRef}
          canvasContextAttributes={{ preserveDrawingBuffer: true }}
          initialViewState={defaultViewState}
          onLoad={onMapLoad}
          onClick={onClick}
          onMouseEnter={onHover}
          onMouseLeave={onHover}
          onMouseMove={onHover}
          onMove={onZoomChange}
          style={{ width: "100%", height: "550px" }}
          mapStyle={orthoStyle as StyleSpecification}
          onSourceData={onMapSourceData}
          interactiveLayerIds={interactiveLayerIds}
          cursor={cursor}
        >
          <NavigationControl position="bottom-right" showCompass={false} />

          {zoom >= 16 &&
            noisePins.map((pin, index) => {
              const iconClass = getIconFromNoiseCategorySlug(pin.category_slug);
              return (
                <Marker
                  key={`noise-pin-${index}`}
                  longitude={pin.geometry_point[0]}
                  latitude={pin.geometry_point[1]}
                  anchor="center"
                >
                  <div
                    style={{
                      backgroundColor: "#000091",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      border: "2px solid white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title={`${pin.label} (${pin.category_name}) - ${Math.round(
                      pin.distance,
                    )}m`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNoisePinClick(pin);
                    }}
                  >
                    <i
                      className={`${iconClass} fr-icon--sm`}
                      style={{
                        color: "white",
                        fontSize: "16px",
                      }}
                    />
                  </div>
                </Marker>
              );
            })}
        </Map>

        <NoisePinModal selectedNoisePin={selectedNoisePin} />
      </div>
    );
  },
);

const useStyles = tss.create(() => ({
  container: {
    position: "relative",
  },
  bigAlert: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 0,
    left: 0,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    height: "100vh",
    width: "100vw",
    zIndex: 9999,
  },
}));

export default MapComponent;
